import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { formatCents, localDate, parseDollarsToCents } from '@crave/shared';
import { ApiError } from '@crave/api-client';
import { Button, Card, Field, FormGrid, SelectField } from '@crave/ui';
import { api } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { Notice, Section, Table, money } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';

interface Sales {
  by_day: {
    service_date: string;
    orders: number;
    pizzas: number;
    food_cents: number;
    tax_cents: number;
    tip_cents: number;
    comp_cents: number;
    birthday_cents: number;
    credit_redeemed_cents: number;
    fee_cents: number;
    refunded_cents: number;
    canceled: number;
    no_shows: number;
  }[];
  by_item: { name: string; qty: number; revenue_cents: number }[];
}
interface Assumptions {
  location_id: string;
  packaging_cents_per_pizza: number | null;
  labor_cents_per_service: number | null;
  overhead_cents_per_month: number | null;
  services_per_month: number | null;
  pizzas_per_service_estimate: number | null;
  card_fee_bps: number | null;
  card_fee_fixed_cents: number | null;
}
interface Economics {
  fee_source: 'actual' | 'assumption' | null;
  pizzas_per_service: number | null;
  actuals_90d: { orders: number; pizzas: number; services: number };
  items: {
    slug: string;
    name: string;
    price_cents: number | null;
    food_cost_cents: number | null;
    food_cost_complete: boolean;
    packaging_cents: number | null;
    card_fee_cents: number | null;
    contribution_cents: number | null;
    contribution_pct: number | null;
    labor_cents: number | null;
    overhead_cents: number | null;
    fully_loaded_margin_cents: number | null;
    sold_90d: number;
    missing: string[];
  }[];
}
interface CompBudget {
  id: string;
  location_id: string;
  /** YYYY-MM-DD, always the first of the month. */
  month: string;
  budget_cents: number;
  manager_limit_cents: number;
  used_cents: number;
  remaining_cents: number;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}

/** DELETE sends no body, so the optional reason travels as ?reason= (the API reads both). */
const withReason = (path: string, reason: string | undefined) =>
  reason ? `${path}?reason=${encodeURIComponent(reason)}` : path;
interface LocationRow {
  id: string;
  slug: string;
  name: string;
  timezone: string;
}

type Msg = { kind: 'ok' | 'error'; text: string; mfa?: boolean } | null;

const dollars = (c: number | null) => (c === null ? '' : (c / 100).toFixed(2));
/** Dollars with optional thousands commas, like "1,200.50". */
const parseMoney = (input: string) => parseDollarsToCents(input.replace(/,(?=\d{3}(\D|$))/g, ''));
/** "2.9", "2,9", "2.9%" all parse; anything else is null. */
function parsePercent(input: string): number | null {
  const t = input.replace(/\s+/g, '').replace(/%$/, '').replace(',', '.');
  if (!/^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(t)) return null;
  return Number(t);
}
const monthLabel = (yyyyMm: string) => {
  const [y, m] = yyyyMm.split('-').map(Number);
  if (!y || !m) return yyyyMm;
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};
const saveError = (e: unknown, mfaText: string): Msg =>
  e instanceof ApiError && e.code === 'MFA_REQUIRED'
    ? { kind: 'error', text: mfaText, mfa: true }
    : { kind: 'error', text: e instanceof ApiError ? e.message : 'Could not save.' };

function MsgNotice({ msg }: { msg: Msg }) {
  if (!msg) return null;
  return (
    <Notice kind={msg.kind}>
      {msg.text}
      {msg.mfa && (
        <>
          {' '}
          <Link to="/settings/security">Open Security</Link> to verify this session.
        </>
      )}
    </Notice>
  );
}

export function Reports() {
  const staff = useStaff();
  const canComps = staff.can('comps.approve');
  const [tab, setTab] = useState<'sales' | 'economics' | 'comps'>('economics');
  const tabs: { id: typeof tab; label: string }[] = [
    { id: 'economics', label: 'Unit economics' },
    { id: 'sales', label: 'Sales' },
    ...(canComps ? [{ id: 'comps' as const, label: 'Comps' }] : []),
  ];
  const current = tab === 'comps' && !canComps ? 'economics' : tab;
  let body: ReactNode;
  if (current === 'sales') body = <SalesReport />;
  else if (current === 'comps') body = <CompsReport />;
  else body = <EconomicsReport />;
  return (
    <Section
      title="Reports"
      right={
        <div style={{ display: 'flex', gap: 8 }}>
          {tabs.map((t) => (
            <Button
              key={t.id}
              variant={current === t.id ? 'primary' : 'secondary'}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </Button>
          ))}
        </div>
      }
    >
      {body}
    </Section>
  );
}

const SALES_PERIODS = [
  ['7', 'Last 7 days'],
  ['30', 'Last 30 days'],
  ['90', 'Last 90 days'],
  ['365', 'Last year'],
] as const;

function SalesReport() {
  const staff = useStaff();
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ['sales', staff.location.slug, days],
    queryFn: async () =>
      (await api.get<Sales>(`/admin/reports/sales?location=${staff.location.slug}&days=${days}`))
        .data,
  });
  if (q.isError) return <Notice kind="error">Could not load sales (owner only).</Notice>;
  return (
    <>
      <FormGrid min={240}>
        <SelectField
          label="Period"
          value={String(days)}
          onChange={(e) => setDays(Number(e.target.value))}
          options={SALES_PERIODS}
        />
      </FormGrid>
      <Table
        head={[
          'Day',
          'Orders',
          'Pizzas',
          'Food',
          'Tax',
          'Tips',
          'Birthday',
          'Credit used',
          'Comps',
          'Card fees',
          'Refunds',
          'Canceled / no-show',
        ]}
        rows={(q.data?.by_day ?? []).map((d) => [
          <span style={{ whiteSpace: 'nowrap' }}>{d.service_date}</span>,
          String(d.orders),
          String(d.pizzas),
          money(d.food_cents),
          money(d.tax_cents),
          money(d.tip_cents),
          money(d.birthday_cents),
          money(d.credit_redeemed_cents),
          money(d.comp_cents),
          money(d.fee_cents),
          money(d.refunded_cents),
          `${d.canceled} / ${d.no_shows}`,
        ])}
        empty="No orders in this period."
      />
      <h3 style={{ margin: 0 }}>By pizza</h3>
      <Table
        head={['Pizza', 'Sold', 'Revenue']}
        rows={(q.data?.by_item ?? []).map((i) => [i.name, String(i.qty), money(i.revenue_cents)])}
        empty="Nothing sold yet."
      />
    </>
  );
}

type EcoField = 'packaging' | 'labor' | 'overhead' | 'services' | 'pizzas' | 'feePct' | 'feeFixed';

function EconomicsReport() {
  const qc = useQueryClient();
  const slug = useStaff().location.slug;
  const a = useQuery({
    queryKey: ['ue-assumptions', slug],
    queryFn: async () =>
      (await api.get<Assumptions>(`/admin/reports/unit-economics/assumptions?location=${slug}`))
        .data,
  });
  const e = useQuery({
    queryKey: ['ue', slug],
    queryFn: async () =>
      (await api.get<Economics>(`/admin/reports/unit-economics?location=${slug}`)).data,
  });
  const [f, setF] = useState<Record<EcoField, string>>({
    packaging: '',
    labor: '',
    overhead: '',
    services: '',
    pizzas: '',
    feePct: '',
    feeFixed: '',
  });
  useEffect(() => {
    const d = a.data;
    if (!d) return;
    setF({
      packaging: dollars(d.packaging_cents_per_pizza),
      labor: dollars(d.labor_cents_per_service),
      overhead: dollars(d.overhead_cents_per_month),
      services: d.services_per_month === null ? '' : String(d.services_per_month),
      pizzas: d.pizzas_per_service_estimate === null ? '' : String(d.pizzas_per_service_estimate),
      feePct: d.card_fee_bps === null ? '' : String(d.card_fee_bps / 100),
      feeFixed: dollars(d.card_fee_fixed_cents),
    });
  }, [a.data]);
  const [errs, setErrs] = useState<Partial<Record<EcoField, string>>>({});
  const [msg, setMsg] = useState<Msg>(null);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put('/admin/reports/unit-economics/assumptions', body),
    onSuccess: () => {
      setMsg({ kind: 'ok', text: 'Saved.' });
      void qc.invalidateQueries({ queryKey: ['ue'] });
      void qc.invalidateQueries({ queryKey: ['ue-assumptions'] });
    },
    onError: (err) =>
      setMsg(saveError(err, 'Two-factor sign-in is required to change unit-economics numbers.')),
  });
  if (a.isError || e.isError)
    return <Notice kind="error">Could not load unit economics (owner only).</Notice>;

  function submit() {
    const next: Partial<Record<EcoField, string>> = {};
    const blank = (k: EcoField) => f[k].trim() === '';
    const cents = (k: EcoField, maxCents: number): number | null => {
      if (blank(k)) return null;
      const c = parseMoney(f[k]);
      if (c === null) next[k] = 'Enter dollars like 1.25.';
      else if (c > maxCents) next[k] = `Must be ${formatCents(maxCents)} or less.`;
      return c;
    };

    const packaging = cents('packaging', 10_000);
    const labor = cents('labor', 1_000_000);
    const overhead = cents('overhead', 10_000_000);
    const feeFixed = cents('feeFixed', 500);

    let services: number | null = null;
    if (!blank('services')) {
      const t = f.services.trim().replace(',', '.');
      services = /^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(t) ? Number(t) : null;
      if (services === null) next.services = 'Enter a number of days, like 12.';
      else if (services <= 0) next.services = 'Must be more than 0.';
      else if (services > 31) next.services = 'Must be 31 or less.';
    }

    let pizzas: number | null = null;
    if (!blank('pizzas')) {
      const t = f.pizzas.trim();
      pizzas = /^\d+$/.test(t) ? Number(t) : null;
      if (pizzas === null) next.pizzas = 'Enter a whole number, like 40.';
      else if (pizzas < 1) next.pizzas = 'Must be at least 1.';
      else if (pizzas > 1000) next.pizzas = 'Must be 1000 or less.';
    }

    let feeBps: number | null = null;
    if (!blank('feePct')) {
      const pct = parsePercent(f.feePct);
      if (pct === null) next.feePct = 'Enter a percent like 2.9.';
      else if (pct > 10) next.feePct = 'Must be 10% or less.';
      else feeBps = Math.round(pct * 100);
    }

    setErrs(next);
    if (Object.keys(next).length > 0) {
      setMsg({ kind: 'error', text: 'Some numbers need fixing. See the boxes marked in red.' });
      return;
    }
    setMsg(null);
    save.mutate({
      location_id: a.data?.location_id,
      packaging_cents_per_pizza: packaging,
      labor_cents_per_service: labor,
      overhead_cents_per_month: overhead,
      services_per_month: services,
      pizzas_per_service_estimate: pizzas,
      card_fee_bps: feeBps,
      card_fee_fixed_cents: feeFixed,
    });
  }

  const input = (k: EcoField) => ({
    value: f[k],
    onChange: (ev: { target: { value: string } }) => setF({ ...f, [k]: ev.target.value }),
    error: errs[k],
  });

  return (
    <>
      <MsgNotice msg={msg} />
      <Card>
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            submit();
          }}
          style={{ display: 'grid', gap: 12 }}
        >
          <strong>Your numbers</strong>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
            Food cost comes from your receipts. Everything else here is what you enter. Leave a box
            blank and the report shows the gap instead of guessing.
          </p>
          <FormGrid min={220}>
            <Field
              label="Box + packaging per pizza ($)"
              inputMode="decimal"
              {...input('packaging')}
            />
            <Field label="Labor per service day ($)" inputMode="decimal" {...input('labor')} />
            <Field
              label="Fixed costs per month ($)"
              inputMode="decimal"
              hint="Insurance, permits, fuel, phone, software."
              {...input('overhead')}
            />
            <Field
              label="Service days per month"
              inputMode="decimal"
              hint="More than 0, up to 31."
              {...input('services')}
            />
            <Field
              label="Expected pizzas per service"
              inputMode="numeric"
              hint="Whole number. Used until real sales exist."
              {...input('pizzas')}
            />
            <Field
              label="Card fee (%)"
              inputMode="decimal"
              hint="Real Stripe fees replace this once orders are captured."
              {...input('feePct')}
            />
            <Field label="Card fee fixed ($)" inputMode="decimal" {...input('feeFixed')} />
          </FormGrid>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button type="submit" disabled={save.isPending || !a.data}>
              {save.isPending ? 'Saving…' : 'Save numbers'}
            </Button>
          </div>
        </form>
      </Card>
      {e.data && (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
          Card fees:{' '}
          {e.data.fee_source === 'actual'
            ? 'real Stripe fees from the last 90 days'
            : e.data.fee_source === 'assumption'
              ? 'your estimate'
              : 'not entered'}{' '}
          · Pizzas per service:{' '}
          {e.data.pizzas_per_service === null
            ? 'not known yet'
            : e.data.pizzas_per_service.toFixed(1)}
          {e.data.actuals_90d.services > 0 ? ' (from real sales)' : ''}
        </p>
      )}
      <Table
        head={[
          'Pizza',
          'Price',
          'Food',
          'Packaging',
          'Card fee',
          'Contribution',
          'Labor',
          'Fixed',
          'Profit per pizza',
          'Sold (90d)',
          'Missing',
        ]}
        wrapColumns={[10]}
        rows={(e.data?.items ?? []).map((i) => [
          i.name,
          money(i.price_cents),
          // Food, contribution and profit are worked out, not typed in, so a gap is "Not known yet".
          i.food_cost_cents === null
            ? 'Not known yet'
            : `${money(i.food_cost_cents)}${i.food_cost_complete ? '' : ' *'}`,
          money(i.packaging_cents),
          money(i.card_fee_cents),
          i.contribution_cents === null
            ? 'Not known yet'
            : `${money(i.contribution_cents)} (${i.contribution_pct ?? 0}%)`,
          money(i.labor_cents),
          money(i.overhead_cents),
          <strong
            style={{
              color: (i.fully_loaded_margin_cents ?? 0) < 0 ? 'var(--crave-red)' : undefined,
            }}
          >
            {money(i.fully_loaded_margin_cents, 'Not known yet')}
          </strong>,
          String(i.sold_90d),
          <span style={{ display: 'inline-block', minWidth: 220 }}>{i.missing.join(', ')}</span>,
        ])}
        empty="No menu items."
      />
      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-soft)' }}>
        * Some ingredients have no receipt yet, so food cost is incomplete. Post a receipt under
        Purchasing.
      </p>
    </>
  );
}

/* ---------------------------------- Comps ---------------------------------- */

/** Largest monthly comp budget the form accepts, as a typo guard. */
const MAX_COMP_BUDGET_CENTS = 10_000_000;
/** Shared column width, so the month picker lines up with the budget column below it. */
const COMP_COL = 260;

function CompsReport() {
  const qc = useQueryClient();
  const slug = useStaff().location.slug;
  const locs = useQuery({
    queryKey: ['admin-locations'],
    queryFn: async () => (await api.get<{ items: LocationRow[] }>('/admin/locations')).data.items,
  });
  const loc = locs.data?.find((l) => l.slug === slug) ?? locs.data?.[0];
  const budgets = useQuery({
    queryKey: ['comp-budgets'],
    queryFn: async () =>
      (await api.get<{ items: CompBudget[] }>('/admin/reports/comps')).data.items,
  });
  const [month, setMonth] = useState(() => localDate(new Date(), 'America/New_York').slice(0, 7));
  const [msg, setMsg] = useState<Msg>(null);
  const { can } = useStaff();
  const canDelete = can('records.delete');
  const [target, setTarget] = useState<CompBudget | null>(null);

  const save = useMutation({
    mutationFn: (body: {
      location_id: string;
      month: string;
      budget_cents: number;
      manager_limit_cents: number;
    }) => api.put('/admin/reports/comps', body),
    onMutate: () => setMsg(null),
    onSuccess: (_r, body) => {
      setMsg({ kind: 'ok', text: `Comp budget saved for ${monthLabel(body.month.slice(0, 7))}.` });
      void qc.invalidateQueries({ queryKey: ['comp-budgets'] });
    },
    onError: (err) => setMsg(saveError(err, 'Two-factor sign-in is required to change comps.')),
  });

  if (locs.isError || budgets.isError)
    return (
      <Notice kind="error">
        Could not load comp budgets.{' '}
        {budgets.error instanceof ApiError ? budgets.error.message : ''}
      </Notice>
    );

  const rows = (budgets.data ?? []).filter((b) => !loc || b.location_id === loc.id);
  const existing = rows.find((b) => b.month.slice(0, 7) === month);
  const validMonth = /^\d{4}-\d{2}$/.test(month);

  return (
    <>
      <MsgNotice msg={msg} />
      <Card>
        <div style={{ display: 'grid', gap: 12 }}>
          <strong>Monthly comp budget</strong>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
            Staff can only comp an order when its month has a budget. Comps stop once the budget is
            used up.
          </p>
          <FormGrid min={COMP_COL}>
            <Field
              label="Month"
              type="month"
              value={month}
              onChange={(ev) => {
                setMonth(ev.target.value);
                setMsg(null);
              }}
              error={validMonth ? undefined : 'Pick a month.'}
            />
          </FormGrid>
          {validMonth && (
            <CompBudgetForm
              key={`${month}:${existing?.id ?? 'new'}:${existing?.budget_cents ?? ''}:${existing?.manager_limit_cents ?? ''}`}
              month={month}
              existing={existing}
              pending={save.isPending}
              disabled={!loc}
              onInvalid={() => setMsg(null)}
              onSave={(budgetCents, limitCents) => {
                if (!loc) return;
                save.mutate({
                  location_id: loc.id,
                  month: `${month}-01`,
                  budget_cents: budgetCents,
                  manager_limit_cents: limitCents,
                });
              }}
            />
          )}
        </div>
      </Card>
      <h3 style={{ margin: 0 }}>Budgets</h3>
      <Table
        head={[
          'Month',
          'Budget',
          'Manager limit per comp',
          'Used',
          'Left',
          ...(canDelete ? [''] : []),
        ]}
        stickyActions={canDelete}
        rowKeys={rows.map((b) => b.id)}
        rows={rows.map((b) => [
          monthLabel(b.month.slice(0, 7)),
          money(b.budget_cents),
          money(b.manager_limit_cents),
          money(b.used_cents),
          <span style={{ color: b.remaining_cents <= 0 ? 'var(--crave-red)' : undefined }}>
            {money(b.remaining_cents)}
          </span>,
          ...(canDelete
            ? [
                b.can_delete === false ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      minHeight: 'var(--touch)',
                      fontSize: 13,
                      color: 'var(--ink-soft)',
                    }}
                  >
                    Can’t delete, comps were given
                  </span>
                ) : (
                  <Button
                    variant="danger"
                    aria-label={`Delete the ${monthLabel(b.month.slice(0, 7))} comp budget`}
                    onClick={() => {
                      setMsg(null);
                      setTarget(b);
                    }}
                  >
                    Delete
                  </Button>
                ),
              ]
            : []),
        ])}
        empty="No comp budgets yet. Set one above so staff can comp orders."
      />
      {target && (
        <ConfirmDelete
          open
          onClose={() => setTarget(null)}
          title={`Delete the ${monthLabel(target.month.slice(0, 7))} comp budget?`}
          description={`Its ${money(target.budget_cents)} budget goes, and staff can’t comp orders in ${monthLabel(target.month.slice(0, 7))} until you set a budget again.`}
          askReason
          onConfirm={async ({ reason }) => {
            await api.delete(withReason(`/admin/reports/comps/${target.id}`, reason));
            setMsg({
              kind: 'ok',
              text: `Deleted the ${monthLabel(target.month.slice(0, 7))} comp budget.`,
            });
            void qc.invalidateQueries({ queryKey: ['comp-budgets'] });
          }}
        />
      )}
    </>
  );
}

function CompBudgetForm({
  month,
  existing,
  pending,
  disabled,
  onInvalid,
  onSave,
}: {
  month: string;
  existing: CompBudget | undefined;
  pending: boolean;
  disabled: boolean;
  onInvalid: () => void;
  onSave: (budgetCents: number, limitCents: number) => void;
}) {
  const [budget, setBudget] = useState(existing ? dollars(existing.budget_cents) : '');
  const [limit, setLimit] = useState(existing ? dollars(existing.manager_limit_cents) : '');
  const [errs, setErrs] = useState<{ budget?: string; limit?: string }>({});

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        const next: { budget?: string; limit?: string } = {};
        const budgetCents = budget.trim() === '' ? null : parseMoney(budget);
        if (budget.trim() === '') next.budget = 'Enter the budget for this month in dollars.';
        else if (budgetCents === null) next.budget = 'Enter dollars like 150 or 150.00.';
        else if (budgetCents > MAX_COMP_BUDGET_CENTS)
          next.budget = `Must be ${formatCents(MAX_COMP_BUDGET_CENTS)} or less.`;

        const limitCents = limit.trim() === '' ? 0 : parseMoney(limit);
        if (limitCents === null) next.limit = 'Enter dollars like 25 or 25.00.';
        else if (budgetCents !== null && limitCents > budgetCents)
          next.limit = 'Must not be more than the monthly budget.';

        setErrs(next);
        if (Object.keys(next).length > 0 || budgetCents === null || limitCents === null) {
          onInvalid();
          return;
        }
        onSave(budgetCents, limitCents);
      }}
      style={{ display: 'grid', gap: 4 }}
    >
      <FormGrid min={COMP_COL}>
        <Field
          label={`Budget for ${monthLabel(month)} ($)`}
          inputMode="decimal"
          value={budget}
          onChange={(ev) => setBudget(ev.target.value)}
          hint={
            existing
              ? `${money(existing.used_cents)} used so far.`
              : 'No budget yet, so comps are blocked.'
          }
          error={errs.budget}
        />
        <Field
          label="Manager limit per comp ($)"
          inputMode="decimal"
          value={limit}
          onChange={(ev) => setLimit(ev.target.value)}
          hint="Largest comp a manager or team member can give without you. Blank or 0 means only you can comp."
          error={errs.limit}
        />
      </FormGrid>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button type="submit" disabled={pending || disabled}>
          {pending ? 'Saving…' : existing ? 'Update budget' : 'Set budget'}
        </Button>
      </div>
    </form>
  );
}
